import { computed, isProxy, isRef, nextTick, ref, toRaw } from 'vue';
import { RParam } from '@/api';
import {
  API_BASE_URL,
  AUTO_VUE_COMPS_REGISTER,
  I18N_CACHE_NAME,
  IS_DEV,
  IS_TRACK_UI_TIPS,
  LZD_MODAL_TYPE_CACHE_NAME,
  TOKEN_CACHE_NAME
} from '@/config/setting';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import CryptoJS from 'crypto-es';
import { EleMessage } from 'ele-admin-plus';

import { ElMessageBox } from 'element-plus';
import SortableJs from 'sortablejs';

import type { ElMessageBoxOptions } from 'element-plus';
import type {
  buildRParamFunction,
  LzdColumn,
  LzdColumns,
  R,
  XEntity
} from '@/api';
import type { ProjectInstTask } from '@/api/bsl/project/instance/task/model';
import type { AxiosResponse } from 'axios';
import type { Filters } from 'ele-admin-plus/es/ele-pro-table/types';
import type { TransferListItem } from 'worker_threads';

const modules = import.meta.glob('/src/views/**/index.vue');
// 存储全局组件 /src/views(/**)/index.vue
const globalComps = new Set<string>();

/** 当前国际化语言 */
const globalI18nLang = ref(localStorage.getItem(I18N_CACHE_NAME) || 'zh_CN');

/** 弹窗模式 */
const globalModalType = ref(
  localStorage.getItem(LZD_MODAL_TYPE_CACHE_NAME) || 'modal'
);

export const isFlexTable = ref<boolean>(
  globalThis.innerHeight > 800 && globalThis.innerWidth > 1000
);

/** 监听 */
globalThis.addEventListener('resize', () => {
  isFlexTable.value =
    globalThis.innerHeight > 800 && globalThis.innerWidth > 1000;
});

/** 设置国际化语言 */
export function setGlobalI18nLang(i18nLang: string) {
  localStorage.setItem(I18N_CACHE_NAME, i18nLang);
  globalI18nLang.value = i18nLang;
}

/** 获得国际化语言 */
export function getGlobalI18nLang() {
  return computed(() => globalI18nLang.value);
}

/** 设置弹窗模式 */
export function setGlobalModalType(lzdModalType: 'modal' | 'drawer') {
  localStorage.setItem(LZD_MODAL_TYPE_CACHE_NAME, lzdModalType);
  globalModalType.value = lzdModalType;
}

/** 获得弹窗模式 */
export function getGlobalModalType() {
  return computed(() => globalModalType.value);
}

/** 根据屏幕尺寸判断是否进行 flex-table 布局 */
export function hasFlexTable() {
  return computed(() => isFlexTable.value);
}

/**
 * 根据屏幕尺寸判断是否进行 fixe 定位，一般用于固定数据表列
 * @param value 列是否固定在左侧或者右侧。 true 表示固定在左侧
 */
export function fixed(value: 'left' | 'right' | true) {
  return hasFlexTable().value ? value : void 0;
}

/**
 * 防抖工具，仅最后一次有效
 *
 * const debounceInstance = debounce();
 *
 * debounceInstance(()=>{}, 500)
 */
export function debounce() {
  return (function () {
    let handleTimer: NodeJS.Timeout | null = null;
    return function (func: () => void, time: number = 500) {
      handleTimer && clearTimeout(handleTimer);
      handleTimer = setTimeout(func, time);
    };
  })();
}
/**
 * 节流工具，减少触发频率
 *
 * const throttleInstance = throttle();
 *
 * 调用
 *
 * throttleInstance(()=>{}, 500)
 */
export function throttle() {
  return (function () {
    let last = 0;
    return function (func: () => void, time: number = 500) {
      const now = +new Date();
      if (now - last > time) {
        func();
        last = now;
      }
    };
  })();
}

/** 获取服务器时间 */
function date() {
  return new Date(dateNow());
}

/** 获取服务器时间戳（毫秒） */
function dateNow() {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', `${API_BASE_URL}/bsl/tenant/date/now`, false); // 同步请求
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.send();

  if (xhr.status === 200) {
    try {
      const result = JSON.parse(xhr.responseText);
      if (result && result.code === 0 && typeof result.data === 'number') {
        return result.data;
      }
    } catch (err) {
      console.error('解析服务器时间失败:', err);
    }
  }
  return Date.now();
}
/** 判断当前时间戳 是否在传入的日期之前 */
function dateNowIsBefore(date: string | number | Date) {
  if (!date) {
    LzdUtil.tipsError('dateNowIsBefore error: date is null');
  }
  return dateNow() < parseDate(date)!.getTime();
}

/** 判断当前时间戳 是否在传入的日期之后 */
function dateNowIsAfter(date: string | number | Date) {
  if (!date) {
    LzdUtil.tipsError('dateNowIsAfter error: date is null');
  }
  return dateNow() > parseDate(date)!.getTime();
}

/** 判断当前时间戳 是否在传入的日期之内 */
function dateNowIsBetween(
  start: string | number | Date,
  end: string | number | Date
) {
  if (!start) {
    LzdUtil.tipsError('dateNowIsBetween error: start is null');
  }
  if (!end) {
    LzdUtil.tipsError('dateNowIsBetween error: end is null');
  }
  const currDateNow = dateNow();
  return (
    currDateNow >= parseDate(start)!.getTime() &&
    currDateNow <= parseDate(end)!.getTime()
  );
}

/**
 * 日期格式化输出
 *
 * dateFormat(new Date(), 'yyyy年MM月dd日')
 *
 * @param date 日期
 * @param format 'yyyy-MM-dd', 'hh:mm:ss', 'yyyy-MM-dd hh:mm:ss', 'yyyy年MM月dd日', 'hh时mm分ss秒', 'yyyy年MM月dd日 hh时mm分ss秒'
 */
export function dateFormat(
  date: string | number | Date,
  format: string = 'yyyy-MM-dd hh:mm:ss'
): string {
  if (typeof date === 'number' || typeof date === 'string') {
    if (typeof date === 'string') {
      date = date
        .replace('年', '/')
        .replace('月', '/')
        .replace('日', '')
        .replace(/-+/g, '/')
        .replace('时', ':')
        .replace('分', ':')
        .replace('秒', '');
    }
    date = new Date(date);
  }
  const o = {
    M: date.getMonth() + 1, // 月份
    d: date.getDate(), // 日
    h: date.getHours(), // 小时
    m: date.getMinutes(), // 分
    s: date.getSeconds(), // 秒
    q: Math.floor((date.getMonth() + 3) / 3), // 季度
    S: date.getMilliseconds() // 毫秒
  };
  let dateStr = format;

  // 年月日
  dateStr = dateStr.replace(/yyyy|YYYY/, String(date.getFullYear()));
  dateStr = dateStr.replace(/MM/, String(o.M > 9 ? o.M : '0' + o.M));
  dateStr = dateStr.replace(/M/g, String(o.M));
  dateStr = dateStr.replace(/dd|DD/, String(o.d > 9 ? o.d : '0' + o.d));
  dateStr = dateStr.replace(/d|D/g, String(o.d));

  // 时分
  dateStr = dateStr.replace(/hh|HH/, String(o.h > 9 ? o.h : '0' + o.h));
  dateStr = dateStr.replace(/h|H/g, String(o.h));
  dateStr = dateStr.replace(/mm/, String(o.m > 9 ? o.m : '0' + o.m));
  dateStr = dateStr.replace(/m/g, String(o.m));

  // 秒
  dateStr = dateStr.replace(/ss/, String(o.s > 9 ? o.s : '0' + o.s));
  dateStr = dateStr.replace(/s/g, String(o.s));

  // 毫秒
  dateStr = dateStr.replace(
    /SSS/,
    String(o.S > 99 ? o.S : o.S > 9 ? '0' + o.S : '00' + o.S)
  );
  dateStr = dateStr.replace(/SS/, String(o.S > 9 ? o.S : '0' + o.S));
  dateStr = dateStr.replace(/S/g, String(o.S));

  return dateStr;
}

/** 日期转换 */
export function parseDate(date?: string | number | Date): Date | null {
  if (!date) {
    return null;
  }
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === 'number') {
    return new Date(date);
  }
  if (typeof date !== 'string') {
    return null;
  }
  date = date
    .replace('年', '/')
    .replace('月', '/')
    .replace('日', '')
    .replace(/-+/g, '/')
    .replace('时', ':')
    .replace('分', ':')
    .replace('秒', '')
    .trim();
  if (date.length === 10) {
    date += ' 00:00:00';
  }
  try {
    return new Date(date);
  } catch (err) {
    console.error(err);
    return null;
  }
}

/** 获取时间戳 */
export function getDatetime(date: string | number | Date): number | null {
  const value = parseDate(date);
  return value ? value.getTime() : null;
}

/** AES 加解密类 key 和 vi 长度必须是16的倍数 */
export class AES {
  private key: CryptoJS.lib.WordArray;
  private vi: CryptoJS.lib.WordArray;
  constructor(key: string, vi: string) {
    this.key = CryptoJS.enc.Utf8.parse(
      atob('bkRrQzFUSHFiZnhPUkx3') + ':' + key
    );
    this.vi = CryptoJS.enc.Utf8.parse(vi);
  }
  public encrypt<T>(data: T) {
    const buff = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(JSONUtil.toStr(data)),
      this.key,
      {
        iv: this.vi,
        padding: CryptoJS.pad.Pkcs7,
        mode: CryptoJS.mode.CBC
      }
    );
    return buff.toString();
  }
  public decrypt<T>(data: string): T | null {
    const buff = CryptoJS.AES.decrypt(data, this.key, {
      iv: this.vi,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC
    });
    return JSONUtil.parse(buff.toString(CryptoJS.enc.Utf8));
  }
}

/** 浏览器指纹ID(访客ID) */
let visitorId: string | null = null;
/** 获取浏览器唯一标识 */
export async function browserId() {
  if (visitorId != null) {
    return visitorId;
  }
  return (visitorId = (await (await FingerprintJS.load()).get()).visitorId);
}

/** 缓存类 */
export class Lun51Storage {
  private aes: AES;
  private storageKeyPrefix = 'lzd:';
  private storage: Storage;
  constructor(isSessionStorage?: boolean) {
    this.aes = new AES(
      atob('NTE2NmIxMTNkYmJjZjE5YzJkNmU5YmRiOTc1NjEzZjA='),
      atob('YUNSVWpJMjlpRnJNdExMVQ==')
    );
    this.storage = isSessionStorage ? sessionStorage : localStorage;
  }
  /** 获取 */
  public get<T>(key: string): T | null {
    try {
      key = this.storageKeyPrefix + this.aes.encrypt(key);
      const data = this.storage.getItem(key);
      if (data == null) {
        return null;
      }
      return this.aes.decrypt(data);
    } catch (err) {
      console.error(err);
    }
    return null;
  }
  /** 添加 */
  public set<T>(key: string, data: T) {
    key = this.storageKeyPrefix + this.aes.encrypt(key);
    this.storage.setItem(key, this.aes.encrypt(data));
  }
  /** 删除 */
  public remove(key: string) {
    key = this.storageKeyPrefix + this.aes.encrypt(key);
    this.storage.removeItem(key);
  }
  /** 清除 */
  public clear(): void {
    const keys: string[] = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      key && key.startsWith(this.storageKeyPrefix) && keys.push(key);
    }
    keys.forEach((k) => this.storage.removeItem(k));
  }
}

/** 缓存工具 */
export const StorageUtil = new Lun51Storage(
  !navigator.userAgent.toLowerCase().includes(' mobile ')
);
/** JSON 序列化工具 */
export const JSONUtil = {
  /** 输出json字符串  */
  toStr<T>(data: T): string {
    if (isRef(data)) {
      data = data.value as T;
    }
    data = toRaw(data);
    if (data instanceof Map) {
      return JSON.stringify(Object.fromEntries(data));
    }
    return JSON.stringify(data);
  },

  /** json字符串 转换 */
  parse<T>(data: unknown): T | null {
    try {
      if (data == null || typeof data === 'undefined' || data === '') {
        return null;
      }
      return (typeof data === 'string' ? JSON.parse(data) : data) as T | null;
    } catch (err) {
      console.error(err);
      console.error('parse error:', data);
    }
    return null;
  }
};

/** 记住账号 */
export async function remember(
  tenantId: string,
  account?: string,
  clear?: boolean
) {
  if (!tenantId) {
    return '';
  }
  const lsKey = `lzd:${tenantId}:remember:account`.toLowerCase();
  if (clear) {
    localStorage.removeItem(lsKey);
    return;
  }
  const aesKey = dataToMd5Str((await browserId()) + tenantId);
  try {
    const aes = new AES(aesKey, aesKey.substring(16));
    // 读取
    if (!account) {
      let value = localStorage.getItem(lsKey);
      if (!value) {
        return '';
      }
      value = aes.decrypt(value);
      return value;
    } else {
      const value = aes.encrypt(account);
      localStorage.setItem(lsKey, value);
    }
  } catch (_) {
    return '';
  }
}

/** MD5加密，返回字符串 */
export function dataToMd5Str(data: any) {
  return CryptoJS.MD5(data).toString();
}

/** SHA256加密，返回字符串 */
export function dataToSha256Str(data: any) {
  return CryptoJS.SHA256(data).toString();
}

/** 填充 appId、dataScopeId、roleCode */
export function formDataFillCurrInfo(formData: FormData) {
  const data: any = JSONUtil.parse((formData.get('param') as any) ?? '{}');
  dataFillCurrInfo(data);
  formData.set('param', JSONUtil.toStr(data));
  return formData;
}

/** 填充 appId、dataScopeId、roleCode */
export function dataFillCurrInfo(data: Record<string, any>) {
  data.appId = data.appId || getAppId();
  data.dataScopeId = data.dataScopeId ?? getDataScopeId();
  data.roleCode = data.roleCode ?? getRoleCode();
  return data;
}

/** 获取缓存的token */
export function getToken(): string | null {
  return StorageUtil.get(TOKEN_CACHE_NAME);
}

/** 获取当前机构标识 */
export function getTenantId(): string | null {
  return StorageUtil.get('currTenantId');
}

/** 获取当前应用标识 */
export function getAppId(): string {
  return StorageUtil.get('currAppId') || 'GP';
}

/** 获取当前学年标识 */
export function getDataScopeId(): number {
  return StorageUtil.get('currDataScopeId') ?? 20212022;
}

/** 获取当前角色标识 */
export function getRoleCode(): string {
  return StorageUtil.get('currRoleCode') ?? 'student';
}

/** 判断是否为论无忧管理员 */
export function isLun51Admin(): Boolean {
  return 'LUN51' === getTenantId() && 'admin' === getRoleCode();
}

/** 调试模式 */
export const isLzdDev = IS_DEV
  ? true
  : new URLSearchParams(location.search).get('lzd-dev') === 'true';

/** 处理 Axios 请求响应*/
export async function requestResult<T>(
  res: AxiosResponse<R<T>>
): Promise<R<T>> {
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 构造 i18nColumns 列
 */
export function i18nColumns(columns: LzdColumns) {
  return computed<LzdColumns>(() => {
    const i18nValue = globalI18nLang.value;
    return columns.map((column) => {
      if (!column.i18nLabel) {
        return { ...column, label: column.label };
      }
      const label = (() => {
        switch (i18nValue) {
          case 'zh_TW':
            return column.i18nLabel.tw ?? column.i18nLabel.cn;
          case 'en':
            return column.i18nLabel.en ?? column.i18nLabel.cn;
          default:
            return column.i18nLabel.cn;
        }
      })();
      return { ...column, label };
    });
  });
}

/** 获取表单 */
export function i18nFormItemLabel(
  columns: LzdColumns,
  prop: string,
  fn?: (item: LzdColumn) => void
) {
  if (!columns || !prop) {
    return void 0;
  }
  const results = columns.filter((v) => v.prop === prop);
  if (results.length === 0) {
    return void 0;
  }
  const result = results[0];
  if (fn) {
    fn(result);
  }
  if (result.placeholder) {
    return result.placeholder;
  }
  if (result.label || !result.i18nLabel) {
    return result.label ?? '';
  }
  const i18nValue = globalI18nLang.value;
  if ('zh_TW' === i18nValue) {
    return (
      result.i18nLabel.tw ?? result.i18nLabel.cn ?? result.i18nLabel.en ?? ''
    );
  }
  if ('en' === i18nValue) {
    return (
      result.i18nLabel.en ?? result.i18nLabel.cn ?? result.i18nLabel.tw ?? ''
    );
  }
  return (
    result.i18nLabel.cn ?? result.i18nLabel.tw ?? result.i18nLabel.en ?? ''
  );
}

/** 获取表单占位提示 */
export function i18nFormItemPlaceholder(columns: LzdColumns, prop: string) {
  const i18nValue = globalI18nLang.value;
  let item: LzdColumn = {};
  const labelStr = i18nFormItemLabel(columns, prop, (v) => (item = v));
  switch (i18nValue) {
    case 'zh_TW':
      return (item.isSelect ? '請選擇' : '請輸入') + labelStr;
    case 'en':
      return (item.isSelect ? 'Select ' : 'Enter ') + labelStr;
    default:
      return (item.isSelect ? '请选择' : '请输入') + labelStr;
  }
}

/** 获取表单输入框类型 数字还是字符串 */
export function i18nFormItemInputType(
  columns: LzdColumns,
  prop: string
): string {
  if (!columns || !prop) {
    return 'text';
  }
  const results = columns.filter((v) => v.prop === prop);
  if (results.length === 0) {
    return 'text';
  }
  return results[0].isNumber
    ? 'number'
    : results[0].isRows
      ? 'textarea'
      : 'text';
}

/**
 * 数据填充
 * @param form 数据源
 * @param to 目标
 * @param keys 需要填充的键，为空全部填充
 */
export function copyFormDataByKeys(
  form: Record<string, any>,
  to: Record<string, any>,
  ...keys: string[]
) {
  keys = keys.length !== 0 ? keys : Object.keys(form);
  keys.forEach((k) => {
    const v = form[k];
    if (typeof v !== 'undefined' && v !== null) {
      to[k] = form[k];
    }
  });
  return to;
}

/**
 * 数据填充
 * @param form 数据源
 * @param to 目标
 * @param excludeKeys 排除的键
 */
export function copyFormDataByExcludeKeys(
  form: Record<string, any>,
  to: Record<string, any>,
  ...excludeKeys: string[]
) {
  if (excludeKeys.length === 0) {
    return copyFormDataByKeys(form, to);
  }
  const keys = Object.keys(form).filter((v) => !excludeKeys.includes(v));
  return copyFormDataByKeys(form, to, ...keys);
}

/**
 * 数据填充(用于添加)
 * @param form 数据源
 * @param to 目标
 * @param excludeKeys 排除的键，主键/创建/更新/禁用/排序/备注等相关字段也会排除
 */
export function copyFormDataNewByExcludeKeys(
  form: Record<string, any>,
  to: Record<string, any>,
  ...excludeKeys: string[]
) {
  const keys = Object.keys(form).filter((v) => !excludeKeys.includes(v));
  keys.forEach((k) => {
    if (k === 'seq') {
      return;
    }
    if (k.startsWith('create')) {
      return;
    }
    if (k.startsWith('update')) {
      return;
    }
    if (k.startsWith('delete')) {
      return;
    }
    if (k.startsWith('disable')) {
      return;
    }
    if (k.startsWith('version')) {
      return;
    }
    if (k.startsWith('comments')) {
      return;
    }
    if (k.startsWith('sortNumber')) {
      return;
    }
    const v = form[k];
    if (typeof v !== 'undefined' && v !== null) {
      to[k] = form[k];
    }
  });
  return to;
}

/* 保存弹窗实例 */
export interface SaveLoadingInstance {
  el: HTMLDivElement;
  success: (message: string) => void;
  error: (errMessage: string) => void;
  close: () => void;
}

let SAVE_LOADING_Z_INDEX = 10000000;
/**
 * 保存弹窗
 * @param message 提示文本
 * @param model 添加透明遮罩
 * @param millis 自动销毁时间（单位毫秒）
 * @returns
 */
export function saveLoading(
  message: string,
  model: boolean = false,
  millis?: number
): SaveLoadingInstance {
  SAVE_LOADING_Z_INDEX++;

  const el = document.createElement('div');
  el.className = 'lzd-loading';
  el.style.zIndex = String(SAVE_LOADING_Z_INDEX);

  const icons = {
    load: `<svg style="width:1em;height:1em;" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"><path d="M 22 6 C 34 6 42 14 42 26"></path></svg>`,
    success: `<svg style="width:1em;height:1em;" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M43 11L16.875 37L5 25.1818" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    error: `<svg style="width:1em;height:1em;" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M24 5L2 43H46L24 5Z" fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round"/><path d="M24 35V36" stroke="currentColor" stroke-width="4" stroke-linecap="round"/><path d="M24 19.0005L24.0083 29" stroke="currentColor" stroke-width="4" stroke-linecap="round"/></svg>`
  };

  let timerIndex: NodeJS.Timeout | undefined;

  const elBody = document.createElement('div');
  const elBodyConent = document.createElement('span');
  const elBodyIcon = document.createElement('i');
  elBodyIcon.innerHTML = icons.load;
  elBodyIcon.classList.add('loading');
  elBodyConent.textContent = message;
  elBodyConent.style.marginRight = '5px';

  elBody.appendChild(elBodyConent);
  elBody.appendChild(elBodyIcon);
  el.appendChild(elBody);
  elBody.className = 'lzd-loading-body';

  document.body.appendChild(el);
  let modelEL: HTMLDivElement | null = null;
  if (model) {
    modelEL = document.createElement('div');
    modelEL.classList.add('lzd-loading-model');
    modelEL.style.zIndex = String(SAVE_LOADING_Z_INDEX - 1);
    document.body.appendChild(modelEL);
  }

  const updateContent = (
    content: string,
    icon: 'success' | 'error',
    ms: number = 350
  ) => {
    elBodyConent.textContent = content;
    elBodyIcon.innerHTML = icons[icon];
    elBodyIcon.classList.remove('loading');
    timerIndex && globalThis.clearTimeout(timerIndex);
    timerIndex = setTimeout(close, ms);
  };

  const success = (content: string) => {
    elBodyIcon.classList.remove('error');
    updateContent(content, 'success');
  };
  const error = (content: string) => {
    elBodyIcon.classList.add('error');
    updateContent(content, 'error', 1000);
  };
  const close = () => {
    timerIndex && globalThis.clearTimeout(timerIndex);
    el.remove();
    modelEL && modelEL.remove();
  };

  if (typeof millis === 'number' && millis >= 0) {
    timerIndex = setTimeout(close, millis);
  }

  return { el, success, error, close };
}

/** 复制文本内容到剪切板 */
export async function copyTextToClipboard(textContent: string) {
  try {
    await navigator.clipboard.writeText(textContent);
  } catch (err: any) {
    console.error('Failed to copy text to clipboard:', err);
  }
}

const clientTrackUiTimer = ref<NodeJS.Timeout>();
/**
 * 定时加载全局系统参数配置 判断页面是否缓存
 * @param func 手动调用刷新
 */
export async function queryClientTrackUi(func?: (call: () => void) => void) {
  if (!IS_TRACK_UI_TIPS) {
    return;
  }
  // 本地跳过
  globalThis.clearTimeout(clientTrackUiTimer.value);
  clientTrackUiTimer.value = setTimeout(() => {
    globalThis.clearTimeout(clientTrackUiTimer.value);
    if (document.querySelector('#lzd-cloud-client-track-ui')) {
      return;
    }
    fetch(`/admin-ui-version.json?t=${Date.now()}`)
      .then(async (res) => {
        if (!res.ok) {
          return;
        }
        globalThis.clearTimeout(clientTrackUiTimer.value);
        const data = await res.json();
        const cacheKey = `lzd:admin_ui_v_json_x`;
        const admin_ui_version_cache_value = localStorage.getItem(cacheKey);
        const _set_cache = () => {
          localStorage.setItem(cacheKey, JSONUtil.toStr(data));
        };
        const _fn = () => {
          if (func) {
            func(() => {
              _set_cache();
              setTimeout(() => location.reload(), 350);
            });
          } else {
            const i18nLang = localStorage.getItem('i18n-lang') || 'zh_CN';
            const reloadPageTips = {
              zh_CN:
                '系统升级已更新，您的页面存在缓存，建议刷新页面，如果您正在执行编辑操作，点击取消按钮，保存数据后自行刷新页面。',
              zh_TW:
                '系統升級已更新，您的頁面存在快取，建議重新整理頁面，如果您正在執行編輯操作，請點擊取消按鈕，儲存數據後自行重新整理頁面。',
              en: 'The system upgrade has been completed. Your page contains cached data. It is recommended that you refresh the page. If you are currently performing an editing operation, please click the cancel button, save your data, and then manually refresh the page.'
            };
            const titleTips = {
              zh_CN: '页面刷新提醒',
              zh_TW: '頁面刷新提醒',
              en: 'Page Refresh Reminder'
            };
            ElMessageBox.confirm(
              `<div id="lzd-cloud-client-track-ui">${reloadPageTips[i18nLang] || reloadPageTips.zh_CN}</div>`,
              {
                title: titleTips[i18nLang] || titleTips.zh_CN,
                type: 'info',
                draggable: true,
                dangerouslyUseHTMLString: true,
                closeOnClickModal: false
              }
            )
              .then(() => {
                _set_cache();
                setTimeout(() => location.reload(), 350);
              })
              .catch(() => {});
          }
        };

        if (admin_ui_version_cache_value) {
          const dataCache: any = JSONUtil.parse(admin_ui_version_cache_value);
          if (dataCache && dataCache.d < data.d && dataCache.v !== data.v) {
            return _fn();
          }
        } else {
          return _fn();
        }
      })
      .catch((_) => {})
      .finally(() => {
        clientTrackUiTimer.value = setTimeout(
          () => queryClientTrackUi(),
          1000 * 60
        );
      });
  }, 550);
}

/**
 * 查询RSA加密公钥
 */
export async function queryPublicKey() {
  const result = await fetch(
    `${API_BASE_URL}/bsl/system/global/config/query/public-key`
  )
    .then((v) => {
      if (!v.ok) {
        return { code: 1, message: v.statusText };
      }
      return v.json();
    })
    .then((v) => {
      if (v?.code === 0) {
        return v.data;
      }
      LzdUtil.tipsSuccessOrWarning(v);
    })
    .catch((err) => {
      console.error(err);
      return { code: 1, message: '网络错误' };
    });
  return result as string | undefined;
}

/** 获取请求信息 */
export function getRMessage(data?: Partial<R<any>> | string | Error) {
  if (!data) {
    return;
  }
  if (typeof data === 'string') {
    return data as string;
  }
  if (data instanceof Error) {
    return data.message;
  }
  const i18n_lang = localStorage.getItem('i18n-lang') || 'zh_CN';
  if ('zh_TW' === i18n_lang) {
    return data['messageTw'];
  }
  if ('en' === i18n_lang) {
    return data['messageEn'];
  }
  return data['message'];
}

/** 错误提示 */
export function tipsError(data?: Partial<R<any>> | string | Error) {
  data && EleMessage.error(getRMessage(data));
  return void 0;
}

/** 信息提示 */
export function tipsInfo(data?: Partial<R<any>> | string | Error) {
  data && EleMessage.info(getRMessage(data));
  return void 0;
}

/** 成功提示 */
export function tipsSuccess(data?: Partial<R<any>> | string | Error) {
  data && EleMessage.success(getRMessage(data));
  return void 0;
}

/** 警告提示 */
export function tipsWarning(data?: Partial<R<any>> | string | Error) {
  let value: any = null;
  if (
    typeof data === 'object' &&
    'code' in data &&
    'data' in data &&
    Array.isArray(data.data) &&
    data.data.length > 0 &&
    typeof (value = data.data[0]) === 'object' &&
    'errorMsg' in value
  ) {
    data = { ...data };
    data.message = value.errorMsg;
    data.messageEn = value.errorMsgEn;
    data.messageTw = value.errorMsgTw;
  }
  data && EleMessage.warning(getRMessage(data));
  return void 0;
}

/**
 * 成功或警告提示
 * @param data 数据
 * @param okFn 成功回调函数 状态码 code === 0
 */
export function tipsSuccessOrWarning(
  data?: Partial<R<any>> | string | Error,
  okFn?: () => void
) {
  let isOk = false;
  try {
    if (typeof data === 'object' && !(data instanceof Error)) {
      return (isOk = data.code === 0) ? tipsSuccess(data) : tipsWarning(data);
    }
    data && EleMessage.warning(getRMessage(data));
    return void 0;
  } finally {
    if (isOk && okFn) {
      okFn();
    }
  }
}

/** 参数错误提示 */
export function tipsParameterError() {
  tipsError({
    code: 1,
    message: '参数错误',
    messageEn: '參數錯誤',
    messageTw: 'Parameter Error'
  });
  return void 0;
}

/** 确认提示 */
export function tipsConfirm(
  message?: string | { cn: string; en: string; tw?: string },
  options?: ElMessageBoxOptions
) {
  if (!message) {
    return Promise.reject(true);
  }
  return new Promise<boolean>((resolve: (value: boolean) => void) => {
    let msg: string | null = null;
    let title: string = '操作提示';
    const i18n = localStorage.getItem('i18n-lang') || 'zh_CN';
    if (typeof message === 'string') {
      msg = message;
      if ('en' === i18n) {
        title = 'Operation Prompt';
      }
    } else {
      if ('zh_TW' === i18n) {
        msg = message.tw ?? message.cn ?? message.en ?? null;
      } else if ('en' === i18n) {
        msg = message.en ?? message.cn ?? message.tw ?? null;
        title = 'Operation Prompt';
      } else {
        msg = message.cn ?? message.tw ?? message.cn ?? null;
      }
    }
    msg = msg != null ? msg : '确认提交操作请求？';
    ElMessageBox.confirm(msg, title, {
      type: 'warning',
      draggable: true,
      dangerouslyUseHTMLString: true,
      ...options
    })
      .then(() => resolve(true))
      .catch(() => resolve(false));
  });
}

/** 获取随机字符 0-9a-f, 默认8位 */
export function randomStr(length: number = 6) {
  let str = '';
  while (str.length < length) {
    str += Math.random().toString(16).substring(2);
  }
  return str.substring(0, length);
}

/** 构造uuid  yyyyMMddHHmmss-xxxxxx-xxxxxxxx-xxxxxx */
export function buildUuid() {
  return `${dateFormat(new Date(), 'yyyyMMddHHmmss')}-${randomStr(6)}-${randomStr(8)}-${randomStr(6)}`;
}

/** uuid xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx */
export function fastUUID(): string {
  const randomBytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(randomBytes);

  // 设置 version 4 和 variant bits
  randomBytes[6] = (randomBytes[6] & 0x0f) | 0x40; // version 4
  randomBytes[8] = (randomBytes[8] & 0x3f) | 0x80; // variant 1

  // 将 Uint8Array 转为 number[]
  const bytes: number[] = Array.from(randomBytes);

  return [
    bytes.slice(0, 4), // 第一段：xxxxxxxx
    bytes.slice(4, 6), // 第二段：xxxx
    bytes.slice(6, 8), // 第三段：4xxx
    bytes.slice(8, 10), // 第四段：yxxx
    bytes.slice(10, 16) // 第五段：xxxxxxxxxxxx
  ]
    .map((arr) => arr.map((b) => b.toString(16).padStart(2, '0')).join(''))
    .join('-');
}

/** uuid 去除 - */
export function fastSimpleUUID(): string {
  return fastUUID().replace(/-/g, '');
}

/**
 * 解析vue路由组件
 * @param path 组件地址 如 /src/views${path}/index.vue
 */
export async function getVueComponent(path?: string) {
  if (path) {
    const module = modules[`/src/views${path}/index.vue`];
    if (module) {
      return (await (module() as Promise<any>)).default;
    }
    return Promise.reject(`component ${path} not found`);
  }
}

/** 加载 ['/bsl', '/apl'] 下的index.vue组件 */
export async function loadVueComponents() {
  // 已经存在 直接结构返回，避免操作数组影响源数据
  if (globalComps.size > 0) {
    return [...globalComps];
  }
  const keys = Object.keys(modules);
  for (let i = 0; i < keys.length; i++) {
    const path = keys[i];
    if (
      typeof path === 'string' &&
      path.endsWith('/index.vue') &&
      /^\/src\/views\/(bsl)|(apl)\//.test(path)
    ) {
      // 去除两端字符 /src/views  和 /index.vue
      // `/src/views${path}/index.vue`
      const compPath = path
        .replace(/^\/src\/views/, '')
        .replace(/\/index\.vue$/, '');
      globalComps.add(compPath);
    }
  }

  // 自动注册VUE组件
  if (AUTO_VUE_COMPS_REGISTER === 'true') {
    fetch(`${API_BASE_URL}/bsl/system/global/vue/comp/auto/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify([...globalComps])
    }).catch((_) => {});
  }
}

/**
 * 转换为 键值对，类似MAP
 * LzdUtil.toRecord<string, string, any>(
 * [
 *   { id: '1001', name: '计算机科学与技术' },
 *   { id: '1002', name: '物联网工程' }
 * ],
 * (item) => item.id,
 * (item) => item
 * );
 * @param items 数据集合
 * @param func 回调处理
 */
export function toRecord<T, K, V>(
  items: Record<string, T>[],
  keyfn: (value: Record<string, T>) => K,
  valuefn: (value: Record<string, T>) => V
) {
  const result: Record<string, V> = {};
  items.forEach((item) => {
    const key = keyfn(item);
    const value = valuefn(item);
    if (typeof key === 'string' && typeof value !== 'undefined') {
      result[key] = value;
    }
  });
  return result;
}

/**
 * 构建表单验证
 * @param data 表单数据
 * @param fn 构造验证规则函数
 * @param ignoreKeys 不需要验证的
 */
export function buildFormRules<T extends Partial<XEntity>, V>(
  data: T,
  fn: (k: string) => V,
  ...ignoreKeys: string[]
) {
  const formRules: any = {};
  Object.keys(data).forEach((k) => {
    if (ignoreKeys.includes(k)) {
      return;
    }
    formRules[k] = fn(k);
  });
  return formRules;
}

function buildEdit(data: Record<string, any>, fn?: Function) {
  return RParam.buildEdit(data, fn);
}

function buildWhere(data: Record<string, any>, fn?: buildRParamFunction) {
  return RParam.buildWhere(data, fn);
}

function buildWhereFilters(data: Record<string, any>, filters: Filters) {
  return RParam.buildWhereFilters(data, filters);
}

function buildWhereLike(data: Record<string, any>, ...likeFields: string[]) {
  return RParam.buildWhereLike(data, ...likeFields);
}

/** 构建数据表格缓存键值 */
function buildTableCacheKey(key: string) {
  const str = location.pathname || '' + location.search;
  return dataToMd5Str(str + key) + 'Table';
}

/** 构造文件访问链接，避免权限不足/未登录等 */
function buildFileLink(url?: string) {
  if (!url || !/^https?:\/\/.{10,}/.test(url)) {
    return void 0;
  }

  if (url.includes('access_token=')) {
    return url;
  }

  return (url +=
    (url.includes('?') ? '&' : '?') +
    'access_token=' +
    encodeURIComponent(getToken()!));
}

/** 替换参数 */
function replaceParams(params: RParam[], ...newParam: RParam[]) {
  if (!params || !newParam) {
    return;
  }
  let indexOf = -1;
  for (const d of newParam) {
    while ((indexOf = params.findIndex((m) => d.name === m.name)) >= 0) {
      params.splice(indexOf, 1);
    }
    params.push(d);
  }
}

/** 删除参数 */
function deleteParams(params: RParam[], ...keys: string[]) {
  if (!params?.length || !keys.length) {
    return;
  }
  let indexOf = -1;
  for (const k of keys) {
    while ((indexOf = params.findIndex((d) => d.name === k)) >= 0) {
      params.splice(indexOf, 1);
    }
  }
}

// 存储UI缓存记录
const UI_CACHE_DATA: Record<string, string | null> = {};
async function saveUiCacheSetting(key: string, value: string) {
  if (typeof key !== 'string' || typeof value !== 'string') {
    return;
  }
  // 保存函数
  const func = (k: string, v: string) => {
    // 更新缓存变量
    UI_CACHE_DATA[k] = v || null;
    const data: Record<string, any> = { cacheKey: k, cacheValue: v };
    data.dataScopeId = getDataScopeId();
    data.appId = getAppId();
    fetch(`${API_BASE_URL}/bsl/tenant/ui/cache/config/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getToken() ?? ''
      },
      body: JSON.stringify(data)
    }).catch((_) => {});
  };
  // 表格列顺序配置
  if (key.endsWith('TableCols') && key.length > 9) {
    func(key, value);
  }
  // 表格列宽配置
  else if (key.endsWith('TableColsWidth') && key.length > 14) {
    func(key, value);
  }
  // 表行紧凑模式配置
  else if (key.endsWith('TableSize') && key.length > 9) {
    func(key, value);
  }
}

function getUiCacheSettingValue(key: string) {
  if (typeof key !== 'string') {
    return;
  }
  // 保存函数
  const func = (k: string) => {
    if (k in UI_CACHE_DATA) {
      return UI_CACHE_DATA[k];
    }

    const data: Record<string, any> = {};
    data.dataScopeId = getDataScopeId();
    data.appId = getAppId();
    data.where = RParam.buildWhere({ cacheKey: k });
    let result: any = null;
    const xhr = new XMLHttpRequest();
    xhr.open(
      'POST',
      `${API_BASE_URL}/bsl/tenant/ui/cache/config/query/single/value`,
      false
    );
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', getToken() ?? '');
    xhr.send(JSON.stringify(data));
    if (xhr.status === 200 || xhr.status === 201) {
      result = JSON.parse(xhr.responseText);
    }
    let cacheValue: string | null = null;
    if (result && result.code === 0) {
      cacheValue = result.data || null;
    }
    // 存入缓存
    UI_CACHE_DATA[k] = cacheValue;

    return cacheValue;
  };
  // 表格列顺序配置
  if (key.endsWith('TableCols') && key.length > 9) {
    return func(key);
  }
  // 表格列宽配置
  else if (key.endsWith('TableColsWidth') && key.length > 14) {
    return func(key);
  }
  // 表行紧凑模式配置
  else if (key.endsWith('TableSize') && key.length > 9) {
    return func(key);
  }
}

/**
 * 数据表格拖拽排序
 * @param selector 选择器
 * @param tableRef 数据表格vue实例
 * @param callbackSortInst 排序实例回调
 * @param updateSortNumber 排序结果执行函数
 * @param reload 重载数据表格方法
 */
function tableDragDropSort(
  selector: string,
  tableRef: any,
  callbackSortInst: (inst: SortableJs) => void,
  updateSortNumber: (items: XEntity[]) => Promise<unknown>,
  reload: () => void
) {
  if (!selector || !tableRef || !tableRef.value) {
    return;
  }
  nextTick(() => {
    nextTick(() => {
      const tableEl = tableRef.value?.$el;
      if (!tableEl) return;
      const tbodyEl = tableEl.querySelector(
        '.el-table__inner-wrapper > .el-table__body-wrapper .el-table__body > tbody'
      ) as HTMLElement;
      callbackSortInst(
        new SortableJs(tbodyEl, {
          animation: 300,
          handle: selector,
          draggable: '.el-table__row',
          forceFallback: true,
          // 新增：限制只能在同级拖动
          group: {
            name: 'table-group',
            pull: 'clone',
            put: (to, from) => {
              // 获取拖拽行和目标行的父节点
              const dragRow = from.el.querySelector(
                '.el-table__row[draggable="true"]'
              );
              const targetRow = to.el.querySelector(
                '.el-table__row:not(.el-table__row--placeholder)'
              );
              if (!dragRow || !targetRow) return true;

              const dragParentId = dragRow.getAttribute('data-parent');
              const targetParentId = targetRow.getAttribute('data-parent');
              return dragParentId === targetParentId;
            }
          },
          // 新增：拖动时添加父节点标记
          onStart: (evt) => {
            const row = evt.item as HTMLElement;
            const rowData = tableRef.value?.getData()?.[evt.oldIndex!];
            row.setAttribute('data-parent', rowData?.parent || '0');
          },
          onUpdate: async ({ oldIndex, newIndex }) => {
            if (typeof oldIndex !== 'number' || typeof newIndex !== 'number')
              return;

            const data = tableRef.value?.getData() ?? [];
            const rows: XEntity[] = [];
            // 递归读取数据
            const func = (arr: XEntity[], fn: (v: XEntity) => void) => {
              if (!arr) {
                return;
              }
              arr = [...arr];
              arr.forEach((v) => {
                fn(v);
                const childs = (v as any).children as XEntity[];
                if (childs && Array.isArray(childs)) {
                  func(childs, fn);
                }
              });
            };
            func([...data], (v) => rows.push(v));
            // 交换位置
            const a = rows[oldIndex];
            const b = rows[newIndex];
            // 不是同级
            if ((a as any).parent !== (b as any).parent) {
              tableRef.value?.setData([]);
              reload?.();
              return;
            }
            // 删除
            rows.splice(oldIndex, 1);
            // 插入
            rows.splice(newIndex, 0, a);

            // 更新排序号
            rows.forEach((v, index) => ((v as any).sortNumber = index + 1));
            updateSortNumber(rows).then(() => reload?.());
          }
        })
      );
    });
  });

  return;
}

/** 获取审核角色 */
function reviewRoleCodes(docReviewRoles?: string | ProjectInstTask) {
  let roles: string[] = [];
  try {
    if (typeof docReviewRoles === 'object') {
      docReviewRoles = docReviewRoles.docReviewRoles;
    }
    if (!docReviewRoles) {
      return roles;
    }
    if (docReviewRoles.startsWith('[')) {
      (JSONUtil.parse(docReviewRoles) as Record<string, any>[]).forEach(
        (v) =>
          typeof v.role === 'string' &&
          !roles.includes(v.role) &&
          roles.push(v.role)
      );
      return roles;
    }
    roles = docReviewRoles.split(',');
  } catch (err) {
    console.error(err);
  }
  return roles;
}

/** 数组去重 */
function arrayDistinct<T>(items?: T[], key?: string) {
  // 记录需要返回的数据
  const results: T[] = [];
  if (!items || !Array.isArray(items) || items.length === 0) {
    return results;
  }
  // 记录数据是否添加
  const seen = new Set<unknown>();
  if (!key || typeof items[0] !== 'object') {
    items.forEach((item) => {
      if (!seen.has(item)) {
        seen.add(item);
        results.push(item);
      }
    });
    return results;
  }
  items.forEach((item) => {
    const keyValue = item[key];
    if (!seen.has(keyValue)) {
      seen.add(keyValue);
      results.push(item);
    }
  });

  return results;
}

/** 填充KID */
export function kid(
  item: Record<string, any> | Record<string, any>[],
  now?: boolean
) {
  try {
    if (!item || typeof item !== 'object') {
      return;
    }
    if (Array.isArray(item)) {
      item.forEach((v) => kid(v, now));
      return;
    }
    item.kid = now || !item.kid ? LzdUtil.fastSimpleUUID() : item.kid;
    // 段落
    if ('isProParagraph' in item && 'value' in item) {
      kid(item.value, now);
    }
    // 表格
    else if ('isProTable' in item && 'value' in item) {
      const value = item.value;
      if (typeof value === 'object' && 'columns' in value && 'datas' in value) {
        kid(value.columns, now);
        kid(value.datas, now);
      }
    }
    // 列
    else if ('cells' in item) {
      item.rowStyle = item.rowStyle ?? {};
      // kid(item.cells, now);
      item.cells?.forEach((cell) => {
        cell.cellStyle = cell.cellStyle ?? {};
        cell.cellStyle.verticalAlign = cell.cellStyle.verticalAlign ?? 'middle';
        kid(cell, now);
        cell?.children?.forEach((d) => kid(d, now));
      });
    }
    // 否则填充样式
    else {
      item.style = item.style ?? {};
      item.labelStyle = item.labelStyle ?? {};
      item.valueStyle = item.valueStyle ?? {};
    }
  } catch (err) {}
}

/** 数据克隆 */
export function dataClone<T>(
  value: T,
  transfer?: { transfer: Transferable[] }
) {
  if (typeof value === 'undefined') {
    throw new TypeError('传入的原始值 为 undefined');
  }
  if (isRef(value)) {
    throw new TypeError('请传入原始值，而非 Vue ref 对象');
  }
  if (isProxy(value)) {
    value = toRaw(value);
  }
  return globalThis.structuredClone(value, transfer);
}

/**
 * 转换为 'B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'
 * @param size 文件大小
 */
export function convertFileUnits(size?: number) {
  // 处理非数字或负数输入
  if (typeof size !== 'number' || size < 0) {
    return '';
  }

  // 定义单位和对应的字节数
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const base = 1024;

  // 如果是 0，直接返回 "0B"
  if (size === 0) {
    return '0B';
  }

  // 计算应该使用的单位索引
  let unitIndex = 0;
  let tempSize = size;

  while (tempSize >= base && unitIndex < units.length - 1) {
    tempSize /= base;
    unitIndex++;
  }

  // 格式化数值，保留合适的小数位数
  // 例如：如果大于1，保留1位小数；否则保留2位小数。可以根据需要调整。
  let formattedSize: string;
  if (tempSize >= 10) {
    formattedSize = tempSize.toFixed(1); // 大于等于10时保留1位小数
  } else {
    formattedSize = tempSize.toFixed(2); // 小于10时保留2位小数
  }

  // 移除不必要的尾随零和小数点
  formattedSize = parseFloat(formattedSize).toString();

  return formattedSize + units[unitIndex];
}

/** 通知父级页面加载完成 */
export async function postMessagePageLoad() {
  try {
    if (globalThis.parent !== (globalThis as any)) {
      globalThis.parent.postMessage(
        { type: 'LZD_PAGE_LOAD' },
        { targetOrigin: '*' }
      );
    }
  } catch (err) {
    console.error(err);
  }
}

/** 方法集合 */
class LzdUtil {
  static copyTextToClipboard = copyTextToClipboard;
  static setGlobalI18nLang = setGlobalI18nLang;
  static getGlobalI18nLang = getGlobalI18nLang;
  static setGlobalModalType = setGlobalModalType;
  static getGlobalModalType = getGlobalModalType;
  static hasFlexTable = hasFlexTable;
  static fixed = fixed;
  static debounce = debounce;
  static throttle = throttle;
  static date = date;
  static dateNow = dateNow;
  static dateNowIsBefore = dateNowIsBefore;
  static dateNowIsBetween = dateNowIsBetween;
  static dateNowIsAfter = dateNowIsAfter;
  static dateFormat = dateFormat;
  static parseDate = parseDate;
  static getDatetime = getDatetime;

  static jsonToStr = JSONUtil.toStr;
  static parseJson = JSONUtil.parse;

  static storageGet = function (key: string) {
    return StorageUtil.get(key);
  };
  static storageSet = function (key: string, value: any) {
    StorageUtil.set(key, value);
  };

  static storageClear = function () {
    StorageUtil.clear();
  };

  static remember = remember;
  static dataToMd5Str = dataToMd5Str;
  static dataToSha256Str = dataToSha256Str;
  static formDataFillCurrInfo = formDataFillCurrInfo;
  static dataFillCurrInfo = dataFillCurrInfo;

  static getTenantId = getTenantId;
  static getDataScopeId = getDataScopeId;
  static getAppId = getAppId;
  static getRoleCode = getRoleCode;
  static currStudentRole = () => getRoleCode() === 'student';
  static getAuthorizationToken = getToken;

  static requestResult = requestResult;
  static randomStr = randomStr;

  static tipsError = tipsError;
  static tipsInfo = tipsInfo;
  static tipsSuccess = tipsSuccess;
  static tipsWarning = tipsWarning;
  static tipsSuccessOrWarning = tipsSuccessOrWarning;
  static tipsParameterError = tipsParameterError;

  static tipsConfirm = tipsConfirm;
  static tipsMessageBuilder = getRMessage;
  static i18nColumns = i18nColumns;
  static i18nFormItemLabel = i18nFormItemLabel;
  static i18nFormItemPlaceholder = i18nFormItemPlaceholder;
  static i18nFormItemInputType = i18nFormItemInputType;
  static getVueComponent = getVueComponent;
  static loadVueComponents = loadVueComponents;
  static toRecord = toRecord;

  static buildFormRules = buildFormRules;
  // RParam 常用方法扩充，减少 import 导入
  static buildEdit = buildEdit;
  static buildWhere = buildWhere;
  static buildWhereFilters = buildWhereFilters;
  static buildWhereLike = buildWhereLike;
  static buildTableCacheKey = buildTableCacheKey;
  static buildFileLink = buildFileLink;

  static replaceParams = replaceParams;
  static deleteParams = deleteParams;

  static copyFormDataByKeys = copyFormDataByKeys;
  static copyFormDataByExcludeKeys = copyFormDataByExcludeKeys;
  static copyFormDataNewByExcludeKeys = copyFormDataNewByExcludeKeys;
  static saveLoading = saveLoading;
  static queryClientTrackUi = queryClientTrackUi;
  static queryPublicKey = queryPublicKey;

  static saveUiCacheSetting = saveUiCacheSetting;
  static getUiCacheSettingValue = getUiCacheSettingValue;
  static tableDragDropSort = tableDragDropSort;
  static fastUUID = fastUUID;
  static fastSimpleUUID = fastSimpleUUID;
  static reviewRoleCodes = reviewRoleCodes;
  static arrayDistinct = arrayDistinct;
  static kid = kid;
  static dataClone = dataClone;
  static convertFileUnits = convertFileUnits;

  static postMessagePageLoad = postMessagePageLoad;
}

export default LzdUtil;
